// The competitor/trending tracker journey's inputs (PCHAT-APP-VILLOID-01, DES1 "source coverage
// states"). Everything here is the producers' word: what a source reads, whether an adapter is
// admitted, what a run collected and cost. This file shapes it for display and validates what the
// person types; it decides no policy and never calls coverage complete on its own.
export type SourceStatus =
  /** Reads what the starter needs. */
  | { kind: 'available' }
  /** Reads part of it: `gaps` names what it does not yet read (ranking, later pages...). */
  | { kind: 'partial'; gaps: string[] }
  /** No admitted adapter. `requestedOn` is set once the person asked for it. */
  | { kind: 'awaiting_support'; requestedOn: string | null }
  /** Needs a connection. Connect is offered only when connecting would grant the read. */
  | { kind: 'needs_connection'; whose: string; system: string; canConnect: boolean; owner: string };

export type TrackerSource = {
  id: string;
  /** "Shopify · Orders". */
  name: string;
  /** The real fields it reads, as one phrase ("price, stock, category"). */
  fields: string;
  status: SourceStatus;
  chosen: boolean;
};

/** A source can be chosen only when it can collect now (available or partial coverage). */
export const isChoosable = (source: TrackerSource) =>
  source.status.kind === 'available' || source.status.kind === 'partial';

/**
 * How many sources the person chose that can collect now, and how many requested sources will join
 * when supported. Only what was asked for counts: an unchosen source is not collecting, and a source
 * nobody requested is not promised. Awaiting sources never block the others.
 */
export function coverageCounts(sources: readonly TrackerSource[]) {
  return {
    collectNow: sources.filter((s) => isChoosable(s) && s.chosen).length,
    willJoin: sources.filter(
      (s) => s.status.kind === 'awaiting_support' && s.status.requestedOn !== null,
    ).length,
  };
}

/* ------------------------------------------------------------ schedule + budget */
export type ScheduleDraft = {
  /** 24-hour HH:MM. */
  time: string;
  timezone: string;
  /** Whole credits as typed text ("150"), never money (D74). */
  perRunLimit: string;
  monthlyLimit: string;
};

export type ScheduleErrors = Partial<Record<'time' | 'perRun' | 'monthly' | 'order', true>>;
export type CreditRange = { min: number; max: number };
export type CreditRanges = { perRun: CreditRange; monthly: CreditRange };

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/u;
const WHOLE = /^\d{1,9}$/u;

/** Whole credits, or null when the text is not a plain whole number. */
export function wholeCredits(text: string): number | null {
  return WHOLE.test(text) ? Number(text) : null;
}

const outside = (value: number | null, range: CreditRange) =>
  value === null || value < range.min || value > range.max;

export function validateSchedule(
  draft: ScheduleDraft,
  ranges: CreditRanges,
): { ok: boolean; errors: ScheduleErrors } {
  const errors: ScheduleErrors = {};
  if (!TIME.test(draft.time)) errors.time = true;
  const perRun = wholeCredits(draft.perRunLimit);
  const monthly = wholeCredits(draft.monthlyLimit);
  if (outside(perRun, ranges.perRun)) errors.perRun = true;
  if (outside(monthly, ranges.monthly)) errors.monthly = true;
  if (perRun !== null && monthly !== null && monthly < perRun) errors.order = true;
  return { ok: Object.keys(errors).length === 0, errors };
}

/* ------------------------------------------------------ per-source collection (Data tab) */
export type CollectionStatus =
  | { kind: 'collecting'; time: string; lastRead: string | null; rows: number | null }
  | { kind: 'awaiting_support'; requestedOn: string }
  | { kind: 'stopped'; reason: string; canReconnect: boolean; owner: string }
  | { kind: 'skipped_budget'; skipped: number; total: number };

export type SourceCollection = { sourceId: string; name: string; status: CollectionStatus };

/* ---------------------------------------------------------------------- first run */
export type FirstRun = {
  readSources: number;
  notSupported: number;
  /** Measured credits from the usage receipt as display text; null until measured. */
  cost: string | null;
  /** The next scheduled run, already formatted ("tomorrow 07:00"); null when nothing is scheduled. */
  next: string | null;
};

export type TrackerHistory =
  | { kind: 'from_source'; source: string; since: string }
  | { kind: 'starts_today'; date: string };

export type GapReason = 'budget' | 'unsupported' | 'failed' | 'unknown';

/** A chart point. A skipped period is a gap with a reason, never a zero; `value` is exact text. */
export type ChartPoint = {
  label: string;
  /** Canonical numeric text only ("19.90"); the unit travels separately so plotting never parses it. */
  value: string | null;
  unit?: string;
  gap: boolean;
  reason?: GapReason;
};
export const gapPoint = (label: string, reason: GapReason = 'unknown'): ChartPoint => ({
  label,
  value: null,
  gap: true,
  reason,
});

const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/u;

/**
 * Exact canonical numeric text -> a plotting number, or null. The text is read whole: a value with
 * a unit, a thousands separator or trailing characters ("1,234.00", "19.90 kr") is not canonical and
 * is never partly parsed ("1,234.00" must not plot as 1). The caller strips display units itself.
 */
export function plotValue(value: string | null): number | null {
  if (value === null || !PLAIN_NUMBER.test(value)) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Line segments for the chart: consecutive plottable values are joined and a gap or an unplottable
 * value breaks the line, so a skipped or malformed period is never drawn as a number it is not.
 * Plotting positions only; displayed values stay exact text.
 */
export function lineSegments(points: readonly ChartPoint[]): { index: number; value: number }[][] {
  const segments: { index: number; value: number }[][] = [];
  let current: { index: number; value: number }[] = [];
  points.forEach((point, index) => {
    const n = point.gap ? null : plotValue(point.value);
    if (n !== null) current.push({ index, value: n });
    else if (current.length > 0) {
      segments.push(current);
      current = [];
    }
  });
  if (current.length > 0) segments.push(current);
  return segments;
}
